import React from "react";
import { useTranslations } from "next-intl";
import Head from "next/head";
import { WEBPAGE_TITLE } from "@utils/Constants";
import Page from "@components/Layout/page";
import { Leaf } from "@components/Leaf/Leaf";
import { useInView } from "@hooks/useInView";
import styles from "./VorurPage.module.scss";

const CERTIFICATIONS = [
    { src: "/img/vorur/svarunin.png", key: "cert_swan" },
    { src: "/img/vorur/ecolabel_logo.png", key: "cert_eu" },
    { src: "/img/vorur/blue_angel_logo.png", key: "cert_blue" },
    { src: "/img/vorur/bra_logo.png", key: "cert_bra" },
    { src: "/img/vorur/green_seal_logo.png", key: "cert_green" },
];

export default function VorurPage() {
    const t = useTranslations("VorurPage");

    const [heroRef, heroInView] = useInView();
    const [howRef, howInView] = useInView();
    const [certsRef, certsInView] = useInView();
    const [ctaRef, ctaInView] = useInView();

    return (
        <Page fluid bodyClassName={styles.pageBody}>
            <Head>
                <title key="site_title">{"Vörur - " + WEBPAGE_TITLE}</title>
                <meta
                    name="keywords"
                    content="Vörur, umhverfisvottun, innkaup, ISO 14024"
                />
                <meta
                    property="og:title"
                    content={`${WEBPAGE_TITLE} - Vörur`}
                    key="og:title"
                />
                <meta
                    name="og:description"
                    content={t("hero_desc")}
                    key="og:description"
                />
                <meta name="description" content={t("hero_desc")} />
            </Head>

            <div className={styles.wrapper}>
                <Leaf className={styles.leafBg1} plain />
                <Leaf className={styles.leafBg2} plain />
                <Leaf className={styles.leafBg3} plain />

                <section
                    ref={heroRef}
                    className={`${styles.hero} ${styles.revealTarget} ${heroInView ? styles.revealed : ""}`}
                >
                    <div className={styles.heroContent}>
                        <span className={styles.badge}>{t("badge")}</span>
                        <h1 className={styles.heroTitle}>{t("hero_title")}</h1>
                        <p className={styles.heroSubtitle}>
                            {t("hero_subtitle")}
                        </p>
                        <p className={styles.heroDesc}>{t("hero_desc")}</p>
                        <a className={styles.ctaBtn} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>

                <div className={styles.showcase}>
                    <section
                        ref={howRef}
                        className={`${styles.showcaseRow} ${styles.revealTarget} ${howInView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("showcase_how_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("showcase_how_title")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("showcase_how_desc")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.statGrid}>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                            <polyline points="22 4 12 14.01 9 11.01" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_certified")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("stat_certified_desc")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_linked")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("stat_linked_desc")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                            <circle cx="9" cy="7" r="4" />
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_suppliers")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("stat_suppliers_desc")}
                                    </span>
                                </div>
                                <div
                                    className={`${styles.statCard} ${styles.statCardAccent}`}
                                >
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <rect
                                                x="3"
                                                y="3"
                                                width="18"
                                                height="18"
                                                rx="2"
                                                ry="2"
                                            />
                                            <line
                                                x1="3"
                                                y1="9"
                                                x2="21"
                                                y2="9"
                                            />
                                            <line
                                                x1="9"
                                                y1="21"
                                                x2="9"
                                                y2="9"
                                            />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_open")}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section
                        ref={certsRef}
                        className={`${styles.showcaseRow} ${styles.reversed} ${styles.revealTarget} ${certsInView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("certifications_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("certifications_title")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("certifications_desc")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.certGrid}>
                                {CERTIFICATIONS.map((cert) => (
                                    <div
                                        key={cert.key}
                                        className={styles.certCard}
                                    >
                                        <img
                                            className={styles.certLogo}
                                            src={cert.src}
                                            alt={t(cert.key)}
                                        />
                                        <span className={styles.certName}>
                                            {t(cert.key)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                </div>

                <section
                    ref={ctaRef}
                    className={`${styles.bottomCta} ${styles.revealTarget} ${ctaInView ? styles.revealed : ""}`}
                >
                    <div className={styles.bottomCtaContent}>
                        <h2 className={styles.bottomCtaTitle}>
                            {t("bottom_title")}
                        </h2>
                        <p className={styles.bottomCtaDesc}>
                            {t("bottom_desc")}
                        </p>
                        <a className={styles.ctaBtnLight} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>
            </div>
        </Page>
    );
}
